/* eslint-disable max-len */
/**
 * @fileoverview Visual Studio: Compact panel for generating AI images.
 *               PATH: src/components/contentHub/VisualGenerationPanel.js
 *               Layout mirrors VideoGenerationPanel: pills, hover previews, inline toggles.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { httpsCallable } from "firebase/functions";
import { functions } from '../../firebaseConfig';
import { useToast } from '../shared/contexts/ToastContext';
import {
    FiZap, FiLoader, FiImage, FiType, FiUser, FiLayers
} from 'react-icons/fi';
import { doc, onSnapshot, getDoc } from "firebase/firestore";
import { db } from '../../firebaseConfig';
import { TAXONOMY_MIRROR, SMART_AUTO_VIBE } from '../shared/constants/taxonomyMirror';
import VibePreview from '../shared/VibePreview';

const RESOLUTION_MAP = {
    square: { label: 'Square (1:1)', width: 1024, height: 1024 },
    portrait: { label: 'Portrait (4:5)', width: 1080, height: 1350 },
    vertical: { label: 'Vertical (9:16)', width: 1080, height: 1920 },
    landscape: { label: 'Landscape (16:9)', width: 1920, height: 1080 },
};

function getAutoFormat(template, platform) {
    const t = (template || '').toLowerCase();
    if (t.includes('story')) return 'vertical';
    if (t.includes('carousel')) return t.includes('fb') ? 'square' : 'portrait';
    return 'portrait'; // Unified: all standard posts use portrait (4:5)
}

const TEXT_STRATEGIES = [
    { id: 'none', label: 'No Text', icon: FiImage },
    { id: 'smart', label: 'Smart', icon: FiZap },
    { id: 'headline', label: 'Headline', icon: FiType },
    { id: 'user', label: 'Custom', icon: FiUser },
];

const generateImagePromptFn = httpsCallable(functions, 'generateImagePrompt');
const generateImageFromPromptFn = httpsCallable(functions, 'generateImageFromPrompt');
const notifyAdminOfflineFn = httpsCallable(functions, 'notifyAdminOffline');

function VisualGenerationPanel({ project, post, onVisualUpdate, onSetPostPrompt, adjustments, activePlatform, onSettingsChange, sessionSettings, activeSlideIndex, onSlideChange, workflowType, allPosts, onGenerateAllForPlatform, isGeneratingBatch }) {
    const { addToast } = useToast();

    // --- Configuration State ---
    const [isSmartAuto, setIsSmartAuto] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState(TAXONOMY_MIRROR[0].id);
    const [selectedVibe, setSelectedVibe] = useState(SMART_AUTO_VIBE);
    const [selectedFormat, setSelectedFormat] = useState('portrait');
    const [textStrategy, setTextStrategy] = useState('none');
    const [textInput, setTextInput] = useState('');
    const [hoveredVibeId, setHoveredVibeId] = useState(null);
    const [isSmartAutoHovered, setIsSmartAutoHovered] = useState(false);

    // --- Generation State ---
    const [generatedPrompt, setGeneratedPrompt] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [isArchitecting, setIsArchitecting] = useState(false);
    const [generationStep, setGenerationStep] = useState('');
    const [localError, setLocalError] = useState('');
    const [localOffline, setLocalOffline] = useState(false);
    const [notifyingAdmin, setNotifyingAdmin] = useState(false);
    const lastContextRef = useRef({ postId: null, platform: null });
    const editingRef = useRef(false);
    const vibeChangedRef = useRef(false);

    // --- CAROUSEL STATE ---
    const [isCarouselMode, setIsCarouselMode] = useState(false);
    const [carouselSequence, setCarouselSequence] = useState(post.visualSequence?.[activePlatform] || []);
    const carouselSequenceRef = useRef(carouselSequence);

    useEffect(() => { carouselSequenceRef.current = carouselSequence; }, [carouselSequence]);

    // --- Carousel Detection + Prompt Hydration ---
    useEffect(() => {
        const tmpl = adjustments?.templates?.[activePlatform] || 'standard';
        const isCarousel = tmpl.includes('carousel');
        setIsCarouselMode(isCarousel);
        if (isCarousel) {
            setSelectedFormat(activePlatform === 'facebook' ? 'square' : 'portrait');
            setCarouselSequence(post.visualSequence?.[activePlatform] || []);
        } else {
            setGeneratedPrompt(post.visualPrompt || "");
        }
    }, [post.visualPrompt, adjustments, activePlatform, post.visualSequence]);

    // --- Firestore Watcher ---
    useEffect(() => {
        if (!project.id || !post.id) return;
        const postDocRef = doc(db, "projects", project.id, "posts", post.id);
        const unsubscribe = onSnapshot(postDocRef, (snapshot) => {
            if (!snapshot.exists()) return;
            const data = snapshot.data();
            const tmpl = adjustments?.templates?.[activePlatform] || 'standard';
            if (tmpl.includes('carousel')) {
                const freshSeq = data.visualSequence?.[activePlatform] || [];
                const prevSeq = carouselSequenceRef.current;
                const hasVisualChange = freshSeq.some((slide, idx) => {
                    const prevSlide = prevSeq[idx];
                    return (slide.visual?.url && prevSlide && !prevSlide.visual?.url) || (slide.visual?.url && prevSlide?.visual?.url && slide.visual.url !== prevSlide.visual.url);
                });
                setCarouselSequence(freshSeq);
                if (hasVisualChange) onVisualUpdate(post.id, null, activePlatform, data.visualStatus, data.visualSequence);
            } else if (data.visualPrompt && !editingRef.current && !vibeChangedRef.current) {
                setGeneratedPrompt(prev => data.visualPrompt !== prev ? data.visualPrompt : prev);
            }
        });
        return () => unsubscribe();
    }, [project.id, post.id, activePlatform, adjustments, isCarouselMode, onVisualUpdate]);

    // --- Style Restoration ---
    useEffect(() => {
        const contextKey = `${post.id}-${activePlatform}`;
        const lastKey = `${lastContextRef.current.postId}-${lastContextRef.current.platform}`;
        if (contextKey === lastKey) return;
        lastContextRef.current = { postId: post.id, platform: activePlatform };

        const platformVisual = post.visual?.[activePlatform];
        const savedSettings = platformVisual?.metadata?.settings;
        const source = savedSettings || sessionSettings;

        if (source?.vibeId) {
            const isAutoSaved = source.vibeId === 'smart_auto';
            setIsSmartAuto(isAutoSaved);
            setSelectedCategory(source.categoryId || TAXONOMY_MIRROR[0].id);
            const legacyMap = {auto: "smart", poster: "headline"};
            const savedStrategy = source.textStrategy || "none";
            setTextStrategy(legacyMap[savedStrategy] || savedStrategy);
            setTextInput(source.textInput || "");
            if (isAutoSaved) { setSelectedVibe(SMART_AUTO_VIBE); }
            else {
                const cat = TAXONOMY_MIRROR.find(c => c.id === source.categoryId);
                if (cat) { const vibe = cat.vibes.find(v => v.id === source.vibeId); if (vibe) setSelectedVibe(vibe); }
            }
        } else {
            setIsSmartAuto(true); setSelectedCategory(TAXONOMY_MIRROR[0].id); setSelectedVibe(SMART_AUTO_VIBE);
            setTextStrategy('none'); setTextInput('');
        }
        const tmpl = adjustments?.templates?.[activePlatform] || '';
        setSelectedFormat(getAutoFormat(tmpl, activePlatform));
    }, [activePlatform, post.id, post.visual, adjustments, sessionSettings]);

    const effectiveCategoryId = isSmartAuto ? 'professional' : selectedCategory;
    const activeCategoryData = TAXONOMY_MIRROR.find(c => c.id === selectedCategory) || TAXONOMY_MIRROR[0];
    const isAnyLoading = isGenerating || isArchitecting;

    // --- Handlers ---
    const handleGenerateBlueprint = useCallback(async () => {
        setIsArchitecting(true); setLocalError('');
        try {
            const payload = { projectId: project.id, postId: post.id, visualSettings: { categoryId: effectiveCategoryId, vibeId: selectedVibe.id } };
            if (isCarouselMode) payload.carouselPlatform = activePlatform;
            const promptRes = await generateImagePromptFn(payload);
            if (!promptRes.data?.success) throw new Error("Architect failed.");
            if (isCarouselMode) {
                addToast(`Carousel blueprint drafted!`, 'success');
            } else {
                setGeneratedPrompt(promptRes.data.prompt); vibeChangedRef.current = false;
                if (onSetPostPrompt) onSetPostPrompt(promptRes.data.prompt);
                addToast(`Blueprint drafted!`, 'success');
            }
        } catch { setLocalError("Blueprint generation failed."); addToast("Blueprint failed.", 'error'); }
        finally { setIsArchitecting(false); }
    }, [project.id, post.id, isCarouselMode, activePlatform, effectiveCategoryId, selectedVibe, onSetPostPrompt, addToast]);

    // Auto-blueprint for carousel
    useEffect(() => {
        if (isCarouselMode && carouselSequence.length === 0 && !isArchitecting && !isGenerating && post.id) {
            const timer = setTimeout(() => { handleGenerateBlueprint(); }, 800);
            return () => clearTimeout(timer);
        }
    }, [isCarouselMode, carouselSequence.length, isArchitecting, isGenerating, post.id, handleGenerateBlueprint]);

    const handleBatchGenerate = useCallback(async () => {
        if (!isCarouselMode || carouselSequence.length === 0) return;
        setIsGenerating(true); setLocalError('');
        try {
            let successCount = 0;
            for (let i = 0; i < carouselSequence.length; i++) {
                const slide = carouselSequence[i];
                if (!slide?.blueprint || slide.visual?.url) continue;
                setGenerationStep(`Slide ${i + 1}/${carouselSequence.length}...`);
                try {
                    const result = await generateImageFromPromptFn({ projectId: project.id, postId: post.id, prompt: slide.blueprint, platform: activePlatform, slideIndex: i, settings: { categoryId: effectiveCategoryId, vibeId: selectedVibe.id } });
                    if (result.data?.success) successCount++;
                    await new Promise(r => setTimeout(r, 800));
                } catch { /* skip failed slide */ }
            }
            addToast(`Generated ${successCount} slides`, 'success');
        } catch { setLocalError("Batch failed."); addToast("Batch failed.", 'error'); }
        finally { setIsGenerating(false); setGenerationStep(''); }
    }, [isCarouselMode, carouselSequence, project.id, post.id, activePlatform, effectiveCategoryId, selectedVibe, addToast]);

    const handleOneClickGenerate = useCallback(async () => {
        setIsGenerating(true); setLocalError('');
        try {
            setGenerationStep('prompt');
            let currentBasePrompt = generatedPrompt;
            let targetSlideIndex = null;
            if (isCarouselMode) {
                if (carouselSequence.length === 0) {
                    await handleGenerateBlueprint();
                    await new Promise(r => setTimeout(r, 2000));
                    const freshSnap = await getDoc(doc(db, "projects", project.id, "posts", post.id));
                    const freshSeq = freshSnap.data()?.visualSequence?.[activePlatform] || [];
                    if (freshSeq.length === 0) throw new Error("Blueprint failed");
                    setCarouselSequence(freshSeq); currentBasePrompt = freshSeq[0].blueprint; targetSlideIndex = 0;
                } else {
                    currentBasePrompt = carouselSequence[activeSlideIndex]?.blueprint;
                    targetSlideIndex = activeSlideIndex;
                }
            } else if (!currentBasePrompt) {
                const promptRes = await generateImagePromptFn({ projectId: project.id, postId: post.id, visualSettings: { categoryId: effectiveCategoryId, vibeId: selectedVibe.id } });
                if (!promptRes.data?.success) throw new Error("Architect failed.");
                currentBasePrompt = promptRes.data.prompt; setGeneratedPrompt(currentBasePrompt); vibeChangedRef.current = false;
                if (onSetPostPrompt) onSetPostPrompt(currentBasePrompt);
            }
            setGenerationStep('image');
            const resolution = RESOLUTION_MAP[selectedFormat];
            const imgRes = await generateImageFromPromptFn({ projectId: project.id, postId: post.id, prompt: currentBasePrompt, width: resolution.width, height: resolution.height, platform: activePlatform, textStrategy, textInput, slideIndex: targetSlideIndex, settings: { categoryId: effectiveCategoryId, vibeId: selectedVibe.id, format: selectedFormat, textStrategy, textInput } });
            if (imgRes.data?.success) {
                if (!isCarouselMode && imgRes.data.visual) onVisualUpdate(post.id, imgRes.data.visual, activePlatform, imgRes.data.newStatus);
                addToast(isCarouselMode ? `Slide ${targetSlideIndex + 1} generated!` : 'Image generated!', 'success');
            } else throw new Error("Generation failed.");
        } catch (err) {
            const isOffline = err?.code === 'functions/unavailable' || err?.message?.includes('LOCAL_OFFLINE');
            if (isOffline) { setLocalOffline(true); setLocalError("Local machine offline."); addToast("Local machine offline.", 'error'); }
            else { setLocalError("Generation failed."); addToast("Generation failed.", 'error'); }
        } finally { setIsGenerating(false); setGenerationStep(''); }
    }, [project.id, post.id, generatedPrompt, selectedFormat, onVisualUpdate, onSetPostPrompt, addToast, effectiveCategoryId, selectedVibe, textStrategy, textInput, activePlatform, isCarouselMode, carouselSequence, activeSlideIndex, handleGenerateBlueprint]);

    const selectVibe = (vibe, catId, auto = false) => {
        if (isAnyLoading) return;
        setIsSmartAuto(auto);
        if (!auto && catId) setSelectedCategory(catId);
        setSelectedVibe(auto ? SMART_AUTO_VIBE : vibe);
        setGeneratedPrompt(''); vibeChangedRef.current = true;
        if (onSettingsChange) onSettingsChange({ categoryId: auto ? 'professional' : catId, vibeId: auto ? 'smart_auto' : vibe.id, textStrategy, format: selectedFormat });
    };

    return (
        <div className="p-5 bg-warm-gray/30 text-left space-y-4">

            {/* Error */}
            {localError && (
                <div className="text-[11px] text-red-400 bg-red-500/10 p-2.5 rounded-xl border border-red-500/20 flex items-center justify-between">
                    <span className="font-bold">{localError}</span>
                    {localOffline && (
                        <button type="button" onClick={async () => { setNotifyingAdmin(true); try { await notifyAdminOfflineFn(); addToast('Admin notified.', 'success'); } catch { addToast('Failed.', 'error'); } finally { setNotifyingAdmin(false); } }} disabled={notifyingAdmin} className="ml-3 px-2 py-0.5 bg-amber-500/20 text-amber-400 border border-amber-500/30 rounded text-[9px] font-bold uppercase disabled:opacity-50">
                            {notifyingAdmin ? '...' : 'Notify'}
                        </button>
                    )}
                </div>
            )}

            {/* ─── Image Style ─── */}
            <div>
                <label className="block text-[10px] font-bold text-cream/30 uppercase tracking-widest mb-2.5">Image Style</label>

                {/* Smart Auto */}
                <div className="relative mb-3">
                    <div
                        onClick={() => selectVibe(null, null, true)}
                        onMouseEnter={() => setIsSmartAutoHovered(true)}
                        onMouseLeave={() => setIsSmartAutoHovered(false)}
                        className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${isSmartAuto ? 'border-slate-blue bg-slate-blue/10' : 'border-white/10 bg-warm-gray/10 hover:border-slate-blue/30'} ${isAnyLoading ? 'opacity-50 pointer-events-none' : ''}`}
                    >
                        <div className="flex items-center gap-2">
                            <FiZap size={14} className={isSmartAuto ? 'text-slate-blue' : 'text-cream/40'} />
                            <span className={`text-sm font-black uppercase tracking-tight ${isSmartAuto ? 'text-slate-blue' : 'text-cream'}`}>Smart Auto</span>
                            <span className="text-[10px] text-cream/30 ml-auto">AI picks the best style</span>
                        </div>
                    </div>
                    {isSmartAutoHovered && (
                        <div className="absolute z-50 top-full left-0 mt-1 bg-warm-dark border border-white/10 rounded-xl shadow-2xl p-3 pointer-events-none" style={{ minWidth: '320px' }}>
                            <p className="text-[10px] text-cream/40 mb-2">{SMART_AUTO_VIBE.desc}</p>
                            <VibePreview vibeId="smart_auto" type="image" />
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 border-t border-white/5"></div>
                    <span className="text-[9px] text-cream/20 uppercase tracking-widest font-bold">or choose a style</span>
                    <div className="flex-1 border-t border-white/5"></div>
                </div>

                {/* Category pills */}
                <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {TAXONOMY_MIRROR.map(cat => (
                        <button key={cat.id} onClick={() => selectVibe(cat.vibes[0], cat.id)} disabled={isAnyLoading}
                            className={`px-3 py-2 text-[11px] font-bold rounded-xl transition-all ${!isSmartAuto && selectedCategory === cat.id ? 'bg-slate-blue text-warm-dark' : 'bg-warm-gray/20 text-cream/60 border border-white/10 hover:border-slate-blue/30'} disabled:opacity-50`}
                        >{cat.label}</button>
                    ))}
                </div>

                {/* Vibe pills */}
                {!isSmartAuto && (
                    <div>
                        <label className="block text-[9px] font-bold text-cream/20 uppercase tracking-widest mb-2">{activeCategoryData.label} Vibes</label>
                        <div className="flex flex-wrap gap-1.5">
                            {activeCategoryData.vibes.map(vibe => (
                                <div key={vibe.id} className="relative">
                                    <button onClick={() => selectVibe(vibe, selectedCategory)} onMouseEnter={() => setHoveredVibeId(vibe.id)} onMouseLeave={() => setHoveredVibeId(null)} disabled={isAnyLoading}
                                        className={`px-3 py-2 text-[11px] font-bold rounded-xl transition-all ${selectedVibe.id === vibe.id ? 'bg-slate-blue/20 text-slate-blue border border-slate-blue/40' : 'bg-warm-gray/10 text-cream/50 border border-white/5 hover:border-slate-blue/30 hover:text-cream'} disabled:opacity-50`}
                                    >{vibe.label}</button>
                                    {hoveredVibeId === vibe.id && (
                                        <div className="absolute z-50 bottom-full left-0 mb-2 bg-warm-dark border border-white/10 rounded-xl shadow-2xl p-3 pointer-events-none" style={{ minWidth: '320px' }}>
                                            <p className="text-[10px] text-cream/40 mb-2 leading-tight">{vibe.desc}</p>
                                            <VibePreview vibeId={vibe.id} type="image" />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* ─── Text Strategy Toggle (non-carousel only) ─── */}
            {!isCarouselMode && (
                <div>
                    <label className="block text-[10px] font-bold text-cream/30 uppercase tracking-widest mb-2">Text on Image</label>
                    <div className="flex rounded-xl border border-white/10 overflow-hidden">
                        {TEXT_STRATEGIES.map(s => (
                            <button key={s.id} onClick={() => { if (!isAnyLoading) { setTextStrategy(s.id); if (s.id !== 'user') setTextInput(''); if (onSettingsChange) onSettingsChange({ categoryId: effectiveCategoryId, vibeId: selectedVibe.id, textStrategy: s.id, format: selectedFormat }); } }} disabled={isAnyLoading}
                                className={`flex-1 flex items-center justify-center gap-1 px-2 py-2.5 text-[10px] font-bold transition-all ${textStrategy === s.id ? 'bg-slate-blue text-warm-dark' : 'bg-warm-gray/20 text-cream/50 hover:text-cream hover:bg-warm-gray/30'} disabled:opacity-50`}
                                title={s.id === 'none' ? 'Clean image' : s.id === 'smart' ? 'AI adds text if natural' : s.id === 'headline' ? 'AI creates headline' : 'Your exact text'}
                            >
                                <s.icon size={12} />{s.label}
                            </button>
                        ))}
                    </div>
                    {textStrategy === 'user' && (
                        <input type="text" value={textInput} onChange={(e) => setTextInput(e.target.value)} placeholder="Type your text..." className="w-full bg-warm-gray/20 text-cream border border-white/10 rounded-xl text-xs p-2.5 mt-2" />
                    )}
                </div>
            )}

            {/* ─── Generate ─── */}
            <button onClick={handleOneClickGenerate} disabled={isAnyLoading || isGeneratingBatch}
                className="w-full flex items-center justify-center gap-2 py-3.5 bg-slate-blue hover:bg-slate-blue/80 text-cream border border-slate-blue rounded-xl text-sm font-black uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
                {isGenerating ? <FiLoader className="animate-spin" size={16} /> : <FiZap size={16} />}
                {isGenerating ? (generationStep === 'prompt' ? 'Drafting...' : 'Rendering...') : (isCarouselMode ? `Generate Slide ${(activeSlideIndex || 0) + 1}` : 'Generate Image')}
            </button>


            {/* Carousel batch */}
            {isCarouselMode && carouselSequence.length > 0 && (
                <button onClick={handleBatchGenerate} disabled={isAnyLoading}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-warm-gray/20 hover:bg-warm-gray/30 text-cream/50 border border-white/10 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                >
                    <FiLayers size={13} /> Generate All Slides
                </button>
            )}

            {/* Stream batch */}
            {workflowType === 'stream' && allPosts && allPosts.length > 1 && (
                <button onClick={() => onGenerateAllForPlatform(activePlatform)} disabled={isAnyLoading || isGeneratingBatch}
                    className="w-full flex items-center justify-center gap-2 py-2.5 bg-warm-gray/20 hover:bg-warm-gray/30 text-cream/50 border border-white/10 rounded-xl text-[11px] font-bold uppercase tracking-wider transition-colors disabled:opacity-40"
                >
                    <FiZap size={13} /> Generate All Images
                </button>
            )}
        </div>
    );
}

VisualGenerationPanel.propTypes = {
    project: PropTypes.object.isRequired,
    post: PropTypes.object.isRequired,
    onVisualUpdate: PropTypes.func.isRequired,
    onSetPostPrompt: PropTypes.func.isRequired,
    adjustments: PropTypes.object,
    activePlatform: PropTypes.string,
    onSettingsChange: PropTypes.func,
    sessionSettings: PropTypes.object,
    activeSlideIndex: PropTypes.number,
    onSlideChange: PropTypes.func,
    workflowType: PropTypes.string,
    allPosts: PropTypes.array,
    onGenerateAllForPlatform: PropTypes.func,
    isGeneratingBatch: PropTypes.bool,
};

export default VisualGenerationPanel;
